import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { cn } from 'support/helpers/cn/cn';
import { TransactionGraphData } from 'support/types/dtos';
import { DropdownFilter } from 'components/Filters/DropdownFilter';
import { Dropdown, DropdownOptionType } from 'components/Form/Dropdown';

import { getVolumeStats, TRANSACTION_TYPE_OPTIONS } from '../../helpers';
import { DateRange, TransactionVolumeStats, VolumeStats } from '../../types';
import { DashboardCard } from '../components/DashboardCard/DashboardCard';
import { DashboardSection } from '../components/DashboardSection';

type TransactionVolumeProps = {
  dateRangeOptions: Array<DateRange>;
  transactions: TransactionGraphData | undefined;
  volumeDateRange: number;
  previousDateRange: number;
  onUpdateDateRange: (dateRange: number) => void;
  selectedTransactionType: TransactionVolumeStats['type'];
  onSelectTransactionType: (transactionType: TransactionVolumeStats['type']) => void;
  partnerOptions: Array<DropdownOptionType<string>> | undefined;
  isLoadingPartners?: boolean;
  selectedPartner: string | undefined;
  onSelectPartner: (partner: string) => void;
};

export function TransactionVolume({
  dateRangeOptions,
  transactions,
  volumeDateRange,
  previousDateRange,
  onUpdateDateRange,
  selectedTransactionType,
  onSelectTransactionType,
  partnerOptions,
  isLoadingPartners,
  selectedPartner,
  onSelectPartner,
}: TransactionVolumeProps) {
  const { t } = useTranslation();
  const currentLanguage = i18n.language;
  const initialVolumeStats = getVolumeStats(transactions, volumeDateRange, dateRangeOptions[0].interval, t);
  const [volumeStats, setVolumeStats] = useState<VolumeStats>(initialVolumeStats);

  useEffect(() => {
    setVolumeStats(getVolumeStats(transactions, volumeDateRange, previousDateRange, t));
  }, [transactions, volumeDateRange, currentLanguage, previousDateRange]);

  const volumeStatsItems = Object.values(volumeStats);

  // Only offer type filters for message types actually present in the response.
  const availableTransactionTypeOptions = useMemo(
    () =>
      TRANSACTION_TYPE_OPTIONS.filter((option) =>
        Object.values(volumeStats).some((item) => item.type === option.value),
      ),
    [volumeStats],
  );

  const gridColumnsClass =
    (
      {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-3',
        4: 'grid-cols-4',
        5: 'grid-cols-5',
        6: 'grid-cols-6',
      } as const
    )[volumeStatsItems.length] ?? 'grid-cols-6';

  return (
    <DashboardSection
      title={t('dashboard:transactionVolume.title')}
      actions={
        <div className="relative flex flex-wrap gap-2">
          <Dropdown
            className="w-40"
            dropdownDisplayTestId="TransactionVolumeDropdownDataRangeDisplay"
            value={volumeDateRange}
            options={dateRangeOptions}
            onChange={(newDateRange) => {
              onUpdateDateRange(newDateRange);
            }}
          />
          <DropdownFilter
            filterKey="transactionType"
            className="w-52"
            currentValue={selectedTransactionType || ''}
            testId="TransactionVolumeDropdownTransactionTypeDisplay"
            showAllLabel={t('transactions:list.filter.transactionTypeOptions.showAll')}
            dropdownValues={availableTransactionTypeOptions}
            onChange={(_, transactionType) => {
              onSelectTransactionType(transactionType as TransactionVolumeStats['type']);
            }}
          />
          {(isLoadingPartners || partnerOptions) && (
            <DropdownFilter
              filterKey="partner"
              className="w-52"
              currentValue={selectedPartner ?? ''}
              dropdownValues={partnerOptions ?? []}
              showAllLabel={
                isLoadingPartners ? t('common:loading') : t('transactions:list.filter.partnerOptions.showAll')
              }
              testId="TransactionVolumeDropdowSelectedPartnerDisplay"
              onChange={(_, value) => {
                if (!isLoadingPartners) {
                  onSelectPartner(value);
                }
              }}
            />
          )}
        </div>
      }
    >
      <div className={cn('grid', gridColumnsClass)}>
        {volumeStatsItems.map((item) => (
          <DashboardCard
            key={item.name}
            title={item.name}
            value={item.count}
            status={getVolumeStatsStatus(item.type, selectedTransactionType)}
            onClick={() => onSelectTransactionType(item.type)}
          />
        ))}
      </div>
    </DashboardSection>
  );
}

const getVolumeStatsStatus = (
  itemType: TransactionVolumeStats['type'],
  selectedTransactionType: TransactionVolumeStats['type'],
) => {
  if (!selectedTransactionType) {
    return 'normal';
  }
  return itemType === selectedTransactionType ? 'normal' : 'disabled';
};
